import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ensureSession } from '../lib/auth.ts';
import { navigate } from '../lib/router.ts';
import {
  disablePush, enablePush, followApi, pushOnHere, pushSupport, registerServiceWorker,
  type FollowState, type FollowTarget, type NotifySettings,
} from '../lib/follow.ts';
import { count } from './StreamStats.tsx';

/**
 * Follow / Following with the follower count. Works for guests (a guest session starts on the
 * tap). After following, a sheet asks how to be told when the channel goes live. Used on the
 * watch page next to the creator's name; the channel page places it too.
 *
 * `target` is the channel's handle or one of its stream links.
 */
export function FollowButton({
  target, name, tone = 'dark', size = 'md', initialFollowers, className = '',
}: {
  target: FollowTarget;
  /** How people know the channel (display name or @handle), for the sheet. */
  name: string;
  tone?: 'dark' | 'light';
  size?: 'md' | 'sm';
  initialFollowers?: number;
  className?: string;
}) {
  const key = 'handle' in target ? `h:${target.handle}` : `s:${target.streamId}`;
  const [state, setState] = useState<FollowState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sheet, setSheet] = useState<null | { intent: 'email' | 'sms' | null }>(null);

  useEffect(() => {
    let stale = false;
    void followApi.state(target).then(
      (s) => {
        if (stale) return;
        setState(s);
        // Back from signing in to get email or text alerts: reopen the sheet and turn that on.
        const params = new URLSearchParams(location.search);
        const intent = params.get('alerts');
        if (intent && s.following) {
          setSheet({ intent: intent === 'email' || intent === 'sms' ? intent : null });
          params.delete('alerts');
          const rest = params.toString();
          history.replaceState(history.state, '', `${location.pathname}${rest ? `?${rest}` : ''}`);
        }
      },
      () => undefined,
    );
    return () => {
      stale = true;
    };
    // `target` is a new object every render; `key` is its identity.
  }, [key]);

  const followers = state?.followers ?? initialFollowers ?? null;
  const label = followers === null ? null : `${count(followers)} ${followers === 1 ? 'follower' : 'followers'}`;
  const sm = size === 'sm';
  // A creator you blocked: nothing to follow (unblock from your account page).
  if (state?.blocked && !state.following) return null;
  if (state?.self) {
    return label ? <span className={`text-sm ${tone === 'dark' ? 'text-faint-dark' : 'text-muted'} ${className}`}>{label}</span> : null;
  }

  const follow = async () => {
    setBusy(true);
    setError(null);
    try {
      await ensureSession();
      setState(await followApi.follow(target));
      setSheet({ intent: null });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const following = Boolean(state?.following);
  const base = `inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full font-extrabold disabled:opacity-60 ${sm ? 'h-8 px-3.5 text-[13px]' : 'h-10 px-5 text-[15px]'}`;
  const look = following
    ? tone === 'dark' ? 'border-2 border-white/35 text-white hover:bg-white/10' : 'border-2 border-line text-navy hover:bg-mist'
    : 'bg-race text-white hover:bg-race-600';

  return (
    <span className={`inline-flex min-w-0 items-center gap-2.5 ${className}`}>
      <button
        type="button"
        disabled={busy}
        aria-pressed={following}
        onClick={() => (following ? setSheet({ intent: null }) : void follow())}
        className={`${base} ${look}`}
      >
        {following ? (
          <>
            <BellIcon />
            Following
          </>
        ) : (
          'Follow'
        )}
      </button>
      {label && !sm && <span className={`truncate text-sm ${tone === 'dark' ? 'text-faint-dark' : 'text-muted'}`}>{label}</span>}
      {error && <span role="alert" className="text-sm font-bold text-race-300">{error}</span>}
      {sheet && state && createPortal(
        <NotifySheet
          name={name}
          state={state}
          intent={sheet.intent}
          onState={setState}
          onUnfollow={async () => {
            setState(await followApi.unfollow(target));
            setSheet(null);
          }}
          onClose={() => setSheet(null)}
        />,
        document.body,
      )}
    </span>
  );
}

const BellIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);

/** "How should we tell you?" right after Follow, and when tapping Following. */
function NotifySheet({ name, state, intent, onState, onUnfollow, onClose }: {
  name: string;
  state: FollowState;
  intent: 'email' | 'sms' | null;
  onState: (s: FollowState) => void;
  onUnfollow: () => Promise<void>;
  onClose: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const toggleChannel = async () => {
    if (!state.followId) return;
    setError(null);
    try {
      await followApi.setNotify(state.followId, !state.notify);
      onState({ ...state, notify: !state.notify });
    } catch (err) {
      setError((err as Error).message);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center" role="dialog" aria-modal="true" aria-labelledby="follow-title" onClick={onClose}>
      <div className="max-h-[92dvh] w-full max-w-md overflow-y-auto rounded-t-[28px] bg-white p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] text-left text-navy sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
        <p className="text-sm font-extrabold text-ok">✓ You follow {name}</p>
        <p id="follow-title" className="speed-text mt-1 text-2xl">Get told when they go live</p>
        <AlertMethods intent={intent} returnTo={location.pathname} className="mt-4" />
        <Switch
          className="mt-4 border-t border-line pt-4"
          label={`Alerts from ${name}`}
          detail={state.notify ? 'On for this channel' : 'Off: you still follow, no alerts'}
          on={state.notify}
          onChange={() => void toggleChannel()}
        />
        {error && <p role="alert" className="mt-2 text-sm font-bold text-race-600">{error}</p>}
        <button type="button" className="btn-race mt-5 h-12 w-full text-lg" onClick={onClose}>
          DONE
        </button>
        <button type="button" className="mt-3 h-11 w-full text-[15px] font-bold text-muted underline underline-offset-2" onClick={() => void onUnfollow()}>
          Unfollow
        </button>
      </div>
    </div>
  );
}

/**
 * The ways to be told, with only what applies here: this browser (or the Home Screen hint on
 * iPhone/iPad), and email / text for the account's verified address. A guest who wants email or
 * text signs in first and comes back (`returnTo`). Shared with the Account page.
 */
export function AlertMethods({ intent = null, returnTo, className = '' }: { intent?: 'email' | 'sms' | null; returnTo: string; className?: string }) {
  const [settings, setSettings] = useState<NotifySettings | null>(null);
  const [here, setHere] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const support = pushSupport();

  const load = useCallback(async () => {
    const s = await followApi.settings();
    setSettings(s);
    setHere(await pushOnHere(s).catch(() => false));
    return s;
  }, []);

  useEffect(() => {
    // Ready before the tap, so the tap goes straight to the browser's permission prompt.
    if (support === 'supported') void registerServiceWorker();
    void load().then(async (s) => {
      // Just signed in to get email or text: turn it on.
      if (intent === 'email' && s.email.address && !s.email.on) {
        await followApi.setMethods({ email: true });
        await load();
        setDone(`Email alerts on: ${s.email.address}`);
      } else if (intent === 'sms' && s.sms.number && !s.sms.on) {
        await followApi.setMethods({ sms: true });
        await load();
        setDone(`Text alerts on: ${s.sms.number}`);
      }
    }, () => setError('Couldn’t load your alert settings.'));
  }, [load, intent, support]);

  const run = async (what: string, fn: () => Promise<unknown>) => {
    setBusy(what);
    setError(null);
    setDone(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  if (!settings) return <p className={`text-[15px] text-muted ${className}`}>Loading…</p>;
  const signIn = (via: 'email' | 'sms') =>
    navigate(`/signin?via=${via === 'sms' ? 'phone' : 'email'}&next=${encodeURIComponent(`${returnTo}?alerts=${via}`)}`);
  const nothing = !settings.push.available && !settings.email.available && !settings.sms.available;

  return (
    <div className={className}>
      {nothing && <p className="text-[15px] text-muted">Alerts aren’t available yet. You’ll see this channel when you come back.</p>}
      {settings.push.available && support === 'supported' && (
        <Switch
          label="This browser"
          detail={here ? 'A notification on this device' : 'Get a notification on this device'}
          on={here}
          busy={busy === 'push'}
          onChange={() => void run('push', () => (here ? disablePush() : enablePush(settings.push.vapidPublicKey!)))}
        />
      )}
      {settings.push.available && support === 'ios-home-screen' && (
        <div className="rounded-2xl bg-mist px-4 py-3.5 text-[15px] leading-snug">
          <p className="font-extrabold">Alerts on iPhone and iPad</p>
          <p className="mt-1 text-muted">
            Add ZIP2LIVE to your Home Screen first: tap <b className="text-navy">Share</b>, then <b className="text-navy">Add to Home Screen</b>. Open it from there and follow again to turn on alerts.
          </p>
        </div>
      )}
      {settings.email.available && settings.email.address && (
        <Switch label="Email" detail={settings.email.address} on={settings.email.on} busy={busy === 'email'} onChange={() => void run('email', () => followApi.setMethods({ email: !settings.email.on }))} />
      )}
      {settings.email.available && !settings.email.address && !settings.account && (
        <SignInRow label="Email" detail="Sign in with your email to get alerts there" onClick={() => signIn('email')} />
      )}
      {settings.sms.available && settings.sms.number && (
        <Switch label="Text" detail={settings.sms.number} on={settings.sms.on} busy={busy === 'sms'} onChange={() => void run('sms', () => followApi.setMethods({ sms: !settings.sms.on }))} />
      )}
      {settings.sms.available && !settings.sms.number && !settings.account && (
        <SignInRow label="Text" detail="Sign in with your phone number to get texts" onClick={() => signIn('sms')} />
      )}
      {done && <p role="status" className="mt-2 text-sm font-bold text-ok">✓ {done}</p>}
      {error && <p role="alert" className="mt-2 text-sm font-bold text-race-600">{error}</p>}
    </div>
  );
}

function Switch({ label, detail, on, busy, onChange, className = '' }: { label: string; detail: string; on: boolean; busy?: boolean; onChange: () => void; className?: string }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={busy} onClick={onChange} className={`flex w-full items-center justify-between gap-4 py-2.5 text-left disabled:opacity-60 ${className}`}>
      <span className="min-w-0">
        <span className="block font-extrabold">{label}</span>
        <span className="block truncate text-sm text-muted">{detail}</span>
      </span>
      <span aria-hidden className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${on ? 'bg-race' : 'bg-line'}`}>
        <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-[left] ${on ? 'left-6' : 'left-1'}`} />
      </span>
    </button>
  );
}

function SignInRow({ label, detail, onClick }: { label: string; detail: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="flex w-full items-center justify-between gap-4 py-2.5 text-left">
      <span className="min-w-0">
        <span className="block font-extrabold">{label}</span>
        <span className="block text-sm text-muted">{detail}</span>
      </span>
      <span className="shrink-0 rounded-lg bg-navy px-3.5 py-2 text-sm font-extrabold text-white">Sign in</span>
    </button>
  );
}

/** "Notify followers" switch for creators (Go Live and OBS pages). */
export function NotifyFollowersSwitch({ on, followers, onChange, tone = 'dark', disabled }: { on: boolean; followers: number; onChange: (on: boolean) => void; tone?: 'dark' | 'light'; disabled?: boolean }) {
  const who = followers === 1 ? '1 follower' : `${count(followers)} followers`;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label="Notify followers"
      disabled={disabled}
      onClick={() => onChange(!on)}
      className={`flex w-full items-center justify-between gap-4 rounded-2xl px-4 py-3 text-left disabled:opacity-60 ${tone === 'dark' ? 'bg-white/10' : 'bg-mist'}`}
    >
      <span className="min-w-0">
        <span className="block text-[15px] font-extrabold">Notify followers</span>
        <span className={`block text-[13px] ${tone === 'dark' ? 'text-muted-dark' : 'text-muted'}`}>{on ? `Tell your ${who} when you go live` : `Your ${who} won’t be told`}</span>
      </span>
      <span aria-hidden className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${on ? 'bg-race' : tone === 'dark' ? 'bg-white/25' : 'bg-line'}`}>
        <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-[left] ${on ? 'left-6' : 'left-1'}`} />
      </span>
    </button>
  );
}
